// ============================================
// FILE: src/components/HomeScreen.jsx
// ============================================

import React, { useState, useEffect } from 'react';
import { Search, Bell, MapPin, Heart, Gift, Package, Plus, ShoppingCart, LogOut } from 'lucide-react';

import LocationModal from './modals/LocationModal';
import ProductDetailsModal from './modals/ProductDetailsModal';
import { banners, locations, subscriptionPlans,categoryIcons } from '../data/products';
import { getProductImage, getProductEmoji } from '../utils/imageHelpers';

 
const HomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  showLocationModal,
  selectedLocation,
  setSelectedLocation,
  currentBanner,
  setCurrentBanner,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  getCartTotal,
  updateQuantity,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  setFavorites,
  toggleFavorite,
  setActiveTab,
  selectedCategory = 'All',
  setSelectedCategory,
  showToast,
  appSettings,
  handleLogout,
  featureSections = [],
  appTheme = 'blue',
}) => {
    // Header style
    const headerStyle = appTheme === 'white'
      ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
      : { background: 'var(--primary-gradient)', color: 'var(--text-light)' };
  const favArray = Array.isArray(favorites) ? favorites : [];
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;
  const isProductFavorited = (productId) => (
    favArray.some((item) => String(item?.id) === String(productId))
  );
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const productsPerPage = 40;

  // Auto-advance banner carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(timer);
  }, [setCurrentBanner]);

  // Reset to page 1 when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Extract unique categories from products
  const categories = ['All', ...new Set(products.map(p => {
    // Handle both API response (category object) and static data (category string)
    if (typeof p.category === 'object' && p.category?.name) {
      return p.category.name;
    }
    return p.category || 'Uncategorized';
  }).filter(Boolean))];

  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) {
        return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
      }
    }
    return { value: null, unit: product.unit || '' };
  };

  const formatQuantityLabel = (product, qty) => {
    const { value, unit } = getWeightParts(product);
    if (value) {
      const total = (value * qty).toFixed(2).replace(/\.00$/, '');
      const unitLabel = unit ? ` ${unit}` : '';
      return `${qty} (${total}${unitLabel})`;
    }
    return `${qty}`;
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    const normalizedUnit = String(unit).toLowerCase().trim();
    return ['kg', 'gram', 'g'].includes(normalizedUnit);
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = (product) => {
    const { unit } = getWeightParts(product);
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1,2,3,4];
  };

  const formatWeightLabel = (product) => {
    const { value, unit } = getWeightParts(product);
    if (value) {
      return `${value}${unit ? ` ${unit}` : ''}`;
    }
    return product.weight ? String(product.weight) : '';
  };

  const getSelectedQuantity = (productId) => {
    return quantitySelections[productId] || 1;
  };

  const setSelectedQuantity = (productId, qty) => {
    setQuantitySelections((prev) => ({
      ...prev,
      [productId]: qty
    }));
  };

  const handleAddToCartWithQuantity = (product) => {
    const qty = getSelectedQuantity(product.id);
    addToCart(product, qty);
  };
  
 
  return (
    <div className="pb-20 home-atmosphere" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)', minHeight: '100vh' }}>
      {/* Interactive Header */}
      <div
        className="pb-6 rounded-b-3xl shadow-lg"
        style={{
          background: 'var(--primary-gradient)',
          color: 'var(--text-light)'
        }}
      >
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <div className="flex items-center gap-3 min-w-0 pr-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'var(--primary-bg-light)', opacity: 0.9 }}>
              <img
                src={logoSrc}
                alt={appSettings?.appName || 'Application logo'}
                className="w-8 h-8 rounded-xl object-contain"
              />
            </div>
            <div className="min-w-0">
              <h1 className="app-brand-title truncate" style={{ color: 'var(--text-light)' }}>{appSettings?.appName || 'Fresh Milk Delivery'}</h1>
              <p className="text-xs font-medium truncate" style={{ color: 'var(--accent-light)' }}>Fresh picks &amp; daily savings</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-1.5 sm:p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
              {notifications.filter(n => n.unread).length > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 sm:min-w-[18px] sm:h-[18px] bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[9px] sm:text-[10px] font-bold text-white">
                    {notifications.filter(n => n.unread).length}
                  </span>
                </span>
              )}
            </button>

            <button
              onClick={() => setShowFavorites(true)}
              className="relative p-1.5 sm:p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <Heart className="w-5 h-5 sm:w-6 sm:h-6" />
                {favArray.length > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 sm:min-w-[18px] sm:h-[18px] bg-pink-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[9px] sm:text-[10px] font-bold text-white">
                      {favArray.length}
                  </span>
                </span>
              )}
            </button>

            {handleLogout && (
              <button
                onClick={handleLogout}
                className="p-1.5 sm:p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                title="Logout"
              >
                <LogOut className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            )}
          </div>
        </div>


        {/* Location & Greeting */}
     
        
        <div className="px-4 mb-3">
          <p className="text-sm mb-3" style={{ color: 'var(--accent-light)' }}>
            Good Morning! 👋 {userProfile?.name && <span className="font-semibold" style={{ color: 'var(--text-light)' }}>{userProfile.name}</span>}
          </p>
          
          <button 
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <MapPin size={16} />
            <span className="text-sm font-medium">{selectedLocation}</span>
            <span className="text-xs">▼</span>
          </button>
        </div>

        {/* Search Bar Section */}
        <section className="search-bar-section glass-search-section w-full flex justify-center items-center py-2 bg-white/80" style={{ boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }}>
          <div className="w-full max-w-xl px-2">
            <div className="rounded-full flex items-center px-3 py-1.5 shadow-md" style={{ background: 'var(--primary-bg-light)' }}>
              <Search className="theme-search-icon" size={18} />
              <input
                type="text"
                placeholder="Search for fruits, milk, paneer, curd..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 ml-2 outline-none theme-search-input text-sm"
                style={{ color: 'var(--text-main)', background: 'transparent', minHeight: 'unset' }}
              />
            </div>
          </div>
        </section>

       

        {/* Quick Action Buttons */}
        <div className="px-4 mt-4 flex gap-3 overflow-x-auto">
          <button 
            onClick={() => {
              // Scroll to banner section which shows offers
              const bannerElement = document.querySelector('.banner-carousel');
              if (bannerElement) {
                bannerElement.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Gift size={16} />
            <span className="text-sm font-medium">Offers</span>
          </button>
          <button 
            onClick={() => setActiveTab && setActiveTab('orders')}
            className="flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Package size={16} />
            <span className="text-sm font-medium">Track Order</span>
          </button>
          <button 
            onClick={() => setShowSubscription((filteredProducts && filteredProducts.length>0) ? filteredProducts[0] : (products && products.length>0) ? products[0] : null)}
            className="flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <span className="text-sm font-medium">🔔 Subscribe</span>
          </button>
        </div>
      </div>

      {/* Banner Carousel */}
      <div className="px-4 mt-4 mb-6 banner-carousel">
        <div className="relative overflow-hidden rounded-2xl shadow-lg">
          <div 
            className="flex banner-slide"
            style={{ 
              transform: `translateX(-${currentBanner * 100}%)`,
              transition: 'transform 0.5s ease-in-out'
            }}
          >
            {banners.map((banner) => (
              <div
                key={banner.id}
                className={`min-w-full bg-gradient-to-r ${banner.background} p-6 flex items-center justify-between`}
              >
                <div className="flex-1">
                  <span className="bg-white bg-opacity-30 text-white text-xs px-3 py-1 rounded-full font-medium mb-2 inline-block">
                    {banner.offer}
                  </span>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {banner.title}
                  </h2>
                  <p className="text-white text-opacity-90 text-sm mb-3">
                    {banner.subtitle}
                  </p>
                  <button 
                    onClick={() => {
                      const productsElement = document.querySelector('.products-section');
                      if (productsElement) {
                        productsElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-opacity-90 transition-all"
                  >
                    Shop Now
                  </button>
                </div>
                <div className="text-6xl ml-4">
                  {banner.icon}
                </div>
              </div>
            ))}
          </div>
          
          {/* Banner Indicators */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentBanner === index 
                    ? 'bg-white w-6' 
                    : 'bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Feature Sections */}
      {Array.isArray(featureSections) && featureSections.map(section => (
        <div key={section.id} className="px-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            {section.icon && (
              <span
                className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm"
                style={{ backgroundColor: section.color || '#667eea' }}
              >
                <i className={section.icon} />
              </span>
            )}
            <h2 className="text-lg font-bold text-gray-800">{section.name}</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {(section.products || []).map(product => {
              const isFav = isProductFavorited(product.id);
              const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
              const offPercent = hasDiscount
                ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
                : 0;
              return (
                <div
                  key={product.id}
                  className="min-w-[140px] max-w-[140px] rounded-2xl overflow-hidden bg-white shadow-sm flex-shrink-0"
                >
                  <div
                    className="p-4 flex items-center justify-center relative cursor-pointer bg-gray-50"
                    onClick={() => {
                      setSelectedProduct(product);
                      setShowProductDetails(true);
                    }}
                  >
                    {getProductImage(product, 'small')}
                    {hasDiscount && (
                      <span className="absolute top-2 left-2 text-[10px] bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600 text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
                        {offPercent}% OFF
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof toggleFavorite === 'function') toggleFavorite(product);
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full shadow hover:scale-105 transition-transform"
                    >
                      <Heart
                        size={14}
                        className={isFav ? 'text-pink-500' : 'text-gray-400'}
                        fill={isFav ? 'currentColor' : 'none'}
                      />
                    </button>
                  </div>
                  <div className="p-2">
                    <h3
                      className="text-xs font-semibold text-gray-800 mb-1 line-clamp-2 cursor-pointer hover:text-blue-600"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductDetails(true);
                      }}
                    >
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-1 mb-2">
                      {product.sale_price && product.sale_price < product.price ? (
                        <>
                          <span className="text-xs font-bold text-green-600">₹{product.sale_price}</span>
                          <span className="text-[10px] text-gray-400 line-through">₹{product.price}</span>
                        </>
                      ) : (
                        <span className="text-xs font-bold text-emerald-600">₹{product.price}</span>
                      )}
                    </div>
                    <button
                      onClick={() => addToCart(product, 1)}
                      className="w-full text-[11px] bg-blue-500 text-white py-1 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Categories Section */}
      <div className="px-4 mb-6">
        <h2 className="text-lg font-bold mb-4 text-gray-800">Shop by Category</h2>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory && setSelectedCategory(category)}
              className={`flex flex-col items-center min-w-[80px] p-3 rounded-xl transition-all ${
                selectedCategory === category
                  ? 'bg-blue-500 text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-2xl mb-2">
                {categoryIcons[category] || '📦'}
              </span>
              <span className="text-xs font-medium text-center leading-tight">
                {category}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4 products-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800 font-display">
            {selectedCategory === 'All' ? 'Our Products' : selectedCategory}
          </h2>
          <span className="text-sm text-gray-500">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {paginatedProducts.map((product, index) => {
            const isFav = isProductFavorited(product.id);
            const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
            const offPercent = hasDiscount
              ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
              : 0;
            return (
            <div
              key={product.id}
              className="rounded-2xl overflow-hidden relative product-card glass-card"
              style={{ animationDelay: `${(index % 10) * 50}ms` }}
            >
              <div 
                className="p-6 flex items-center justify-center relative cursor-pointer hover:scale-105 transition-transform glass-image"
                onClick={() => {
                  setSelectedProduct(product);
                  setShowProductDetails(true);
                }}
              >
                {getProductImage(product, 'large')}
                {hasDiscount && (
                  <span className="absolute top-3 left-3 text-xs bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600 text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
                    {offPercent}% OFF
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof toggleFavorite === 'function') {
                      toggleFavorite(product);
                    }
                  }}
                  className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
                >
                  <Heart size={18} className={`${isFav ? 'text-pink-500' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} />
                </button>
              </div>

              <div className="p-3">
                <h3 
                  className="font-display font-semibold text-gray-800 text-sm mb-1 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={() => {
                    setSelectedProduct(product);
                    setShowProductDetails(true);
                  }}
                >{product.name}</h3>
                <div className="flex items-center gap-2 mb-2">
                  {product.weight && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      {formatWeightLabel(product)}
                    </span>
                  )}
                  {!product.weight && product.fat && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {product.fat} fat
                    </span>
                  )}
                  {!product.weight && !product.fat && product.brand && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      {product.brand}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  {product.sale_price && product.sale_price < product.price ? (
                    <>
                      <span className="text-sm font-extrabold text-green-600 drop-shadow-sm">
                        ₹{product.sale_price}
                      </span>
                      <span className="text-xs text-gray-400 line-through">₹{product.price}</span>
                    </>
                  ) : (
                    <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">₹{product.price}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {getQuantityOptions(product).map((qty) => (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => setSelectedQuantity(product.id, qty)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                          getSelectedQuantity(product.id) === qty
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                        }`}
                      >
                        {qty}
                      </button>
                    ))}
                    {isWeightUnit(getWeightParts(product).unit) && (
                      <span className="text-xs text-gray-600 ml-1 font-medium">Kg</span>
                    )}
                    {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                      <span className="text-xs text-gray-600 ml-1 font-medium">{formatUnit(getWeightParts(product).unit)}</span>
                    )}
                  </div>
                  {/* Show quantity controls if in cart, else show Add to Cart */}
                  {(() => {
                    const cartItem = cart.find(item => item.id === product.id);
                    if (cartItem && cartItem.quantity > 0) {
                      return (
                        <div className="w-full flex items-center justify-center gap-2 bg-blue-50 border border-blue-300 rounded-lg px-2 py-1 mt-1">
                          <button
                            className="quantity-btn quantity-btn-minus flex items-center justify-center w-8 h-8 rounded-full bg-white border border-blue-300 text-blue-700 text-xl font-bold shadow-sm hover:bg-blue-100 focus:outline-none disabled:opacity-50"
                            disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                            onClick={async () => {
                               if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                                setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                await updateQuantity(product.id, -1);
                                if (typeof window.reloadCart === 'function') {
                                  await window.reloadCart();
                                }
                                setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                              }
                            }}
                            aria-label="Decrease quantity"
                          >
                            <span aria-hidden="true">&#8722;</span>
                          </button>
                          <span className="quantity-value text-base font-bold text-blue-700 w-10 text-center select-none">{quantityLoading[product.id] ? '...' : cartItem.quantity}</span>
                          <button
                            className="quantity-btn quantity-btn-plus flex items-center justify-center w-8 h-8 rounded-full bg-white border border-blue-300 text-blue-700 text-xl font-bold shadow-sm hover:bg-blue-100 focus:outline-none disabled:opacity-50"
                            disabled={!!quantityLoading[product.id]}
                            onClick={async () => {
                              if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                                setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                await updateQuantity(product.id, 1);
                                if (typeof window.reloadCart === 'function') {
                                  await window.reloadCart();
                                }
                                setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                              }
                            }}
                            aria-label="Increase quantity"
                          >
                            <span aria-hidden="true">&#43;</span>
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button
                        onClick={() => handleAddToCartWithQuantity(product)}
                        className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-1"
                      >
                        <Plus size={16} />
                        Add to Cart
                      </button>
                    );
                  })()}
                </div>

                {Boolean(Number(product.is_subscribable)) && (
                  <button
                    onClick={() => setShowSubscription(product)}
                    className="w-full bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors mt-2"
                  >
                    Subscribe & Save
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 mb-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Previous
            </button>
            
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    currentPage === page
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Page Info */}
        {totalPages > 1 && (
          <p className="text-center text-sm text-gray-500 mb-4">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
          </p>
        )}
      </div>


      {/* Floating Checkout Button */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-24 right-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
            {/* Cart Total */}
            <span className="text-xs font-semibold mt-1 text-green-100 bg-green-700 bg-opacity-70 px-2 py-0.5 rounded">
              Total: ₹{typeof getCartTotal === 'function' ? getCartTotal(cart) : cart.reduce((sum, item) => {
                const effectivePrice = (item.sale_price && item.sale_price < item.price) ? item.sale_price : item.price;
                return sum + (effectivePrice * item.quantity);
              }, 0)}
            </span>
          </div>
        </button>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={showProductDetails}
        onClose={() => {
          setShowProductDetails(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
      />
    </div>
  );
};

export default HomeScreen;